import { ReactNode } from 'react';
import { RolePermissions } from '../../users/hooks/useRoleAccess';
import { DateUtils } from '@cleanpro/common-ui';

interface User {
  id: string;
  email: string;
  role?: string;
  fullName?: string;
  isPlatformAdmin?: boolean;
}

interface DashboardLayoutProps {
  children: ReactNode;
  user: User;
  permissions: RolePermissions;
}

export default function DashboardLayout({ children, user }: DashboardLayoutProps) {
  const getDashboardTitle = () => {
    const isAdmin = user?.isPlatformAdmin;
    if (isAdmin) return 'Admin Dashboard';
    if (user?.role === 'manager') return 'Manager Dashboard';
    if (user?.role === 'employee') return 'Employee Dashboard';
    if (user?.role === 'customer') return `Welcome back, ${user.email?.split('@')[0] || 'Customer'}!`;
    return 'Dashboard';
  };

  const getDashboardSubtitle = () => {
    switch (user?.role) {
      case 'customer':
        return "Here's what's happening with your cleaning services";
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Dashboard Header - Hidden on mobile for cleaner UX */}
      <div className="hidden md:flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 data-testid="page-title" className="text-2xl sm:text-3xl font-bold text-gray-900">
            {getDashboardTitle()}
          </h1>
          {getDashboardSubtitle() && (
            <p className="text-gray-600 mt-1 text-sm sm:text-base">{getDashboardSubtitle()}</p>
          )}
        </div>
        <div className="text-sm text-gray-500">
          Last updated: {DateUtils.formatDateLong(new Date())} at {DateUtils.formatTimeFromDate(new Date())}
        </div>
      </div>

      {/* Dashboard Content */}
      {children}
    </div>
  );
}
